import React, { useState, useRef, useEffect } from "react";
import {
  FaSearch,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaMoneyBillWave,
  FaUmbrellaBeach,
  FaPassport,
  FaCamera,
  FaShieldAlt,
  FaUtensils,
  FaLanguage,
  FaFirstAid,
} from "react-icons/fa";
import { MdExpandMore } from "react-icons/md";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { APP_URLS } from "../config/urls.config";

const Travel = () => {
  const [expandedTip, setExpandedTip] = useState(null);
  const [startLocation, setStartLocation] = useState("");
  const [endLocation, setEndLocation] = useState("");
  const startInputRef = useRef(null);
  const endInputRef = useRef(null);
  const mapRef = useRef(null);
  const directionsRendererRef = useRef(null);
  const [map, setMap] = useState(null);

  // Google Places Autocomplete setup (optional - works without it too)
  useEffect(() => {
    const loadScript = (url) => {
      const script = document.createElement("script");
      script.src = url;
      script.async = true;
      script.defer = true;
      document.body.appendChild(script);
    };

    if (!window.google) {
      loadScript(
        `https://maps.googleapis.com/maps/api/js?key=AIzaSyDK4gxfJbbwZck8T2MLRcIhMRZ0ASykajc&libraries=places,geometry`,
      );
    }

    const handleAutocomplete = () => {
      try {
        if (window.google && window.google.maps && window.google.maps.places) {
          if (startInputRef.current) {
            const startAutocomplete =
              new window.google.maps.places.Autocomplete(
                startInputRef.current,
                { 
                  types: ["(cities)"],
                  fields: ["formatted_address", "geometry", "name"]
                },
              );
            startAutocomplete.addListener("place_changed", () => {
              const place = startAutocomplete.getPlace();
              if (place && (place.formatted_address || place.name)) {
                setStartLocation(place.formatted_address || place.name);
              }
            });
          }

          if (endInputRef.current) {
            const endAutocomplete = new window.google.maps.places.Autocomplete(
              endInputRef.current,
              { 
                types: ["(cities)"],
                fields: ["formatted_address", "geometry", "name"]
              },
            );
            endAutocomplete.addListener("place_changed", () => {
              const place = endAutocomplete.getPlace();
              if (place && (place.formatted_address || place.name)) {
                setEndLocation(place.formatted_address || place.name);
              }
            });
          }
        }
      } catch (error) {

      }
    };

    // Wait for script to load (with timeout)
    let attempts = 0;
    const maxAttempts = 50; // 5 seconds max
    const interval = setInterval(() => {
      attempts++;
      if (window.google && window.google.maps && window.google.maps.places) {
        handleAutocomplete();
        clearInterval(interval);
      } else if (attempts >= maxAttempts) {
        clearInterval(interval);

      }
    }, 100);

    return () => clearInterval(interval);
  }, []);

  const [tripResult, setTripResult] = useState(null);
  const [loadingTrip, setLoadingTrip] = useState(false);
  const [tripError, setTripError] = useState("");

  // Nearby Hotels State
  const [nearbyHotels, setNearbyHotels] = useState([]);
  const [hotelsAlongRoute, setHotelsAlongRoute] = useState([]);
  const [loadingHotels, setLoadingHotels] = useState(false);
  const [hotelsError, setHotelsError] = useState("");
  const [hotelPhotos, setHotelPhotos] = useState({});
  const [hotelMinPrices, setHotelMinPrices] = useState({});
  const navigate = useNavigate();

  // Initialize map when tripResult is available
  useEffect(() => {
    if (tripResult && mapRef.current && window.google && !map) {
      const newMap = new window.google.maps.Map(mapRef.current, {
        center: { lat: 20.5937, lng: 78.9629 }, // Center of India
        zoom: 5,
        mapTypeControl: false,
        streetViewControl: false,
      });
      setMap(newMap);
    }
  }, [tripResult, map]);

  // Call Spring Boot backend for trip planning
  const handleTripSearch = async (e) => {
    e.preventDefault();
    setTripError("");
    setTripResult(null);
    setHotelsError("");
    setNearbyHotels([]);
    setHotelsAlongRoute([]);

    if (!startLocation || !endLocation) {
      setTripError("Please enter both starting and destination locations.");
      return;
    }
    setLoadingTrip(true);

    try {
      if (window.google && window.google.maps) {
        if (directionsRendererRef.current) {
          directionsRendererRef.current.setMap(null);
        }
        const directionsService = new window.google.maps.DirectionsService();
        const directionsRenderer = new window.google.maps.DirectionsRenderer({
          map: map,
        });
        directionsRendererRef.current = directionsRenderer;

        const request = {
          origin: startLocation,
          destination: endLocation,
          travelMode: "DRIVING",
        };

        directionsService.route(request, async (result, status) => {
          if (status === "OK") {
            directionsRenderer.setDirections(result);
            const route = result.routes[0];
            const leg = route.legs[0];

            setTripResult({
              distance: leg.distance.text,
              duration: leg.duration.text,
              start_address: leg.start_address,
              end_address: leg.end_address,
              steps: leg.steps.map((step) => ({
                instruction: step.instructions,
                distance: step.distance.text,
                duration: step.duration.text,
                location: step.end_location,
              })),
              polyline: route.overview_polyline,
            });

            // Fetch all hotels and find those along the route
            fetchHotelsAlongRoute(route);
            // Also fetch hotels specifically at destination as before
            fetchNearbyHotels(endLocation);
          } else {
            setTripError("Could not calculate route: " + status);
          }
          setLoadingTrip(false);
        });
      } else {
        // Fallback if google maps not loaded
        const url = `/api/plan-trip?from=${encodeURIComponent(
          startLocation,
        )}&to=${encodeURIComponent(endLocation)}`;
        const response = await fetch(url);
        const data = await response.json();
        if (data.status === "OK" && data.routes && data.routes.length > 0) {
          const route = data.routes[0];
          const leg = route.legs[0];
          setTripResult({
            distance: leg.distance.text,
            duration: leg.duration.text,
            start_address: leg.start_address,
            end_address: leg.end_address,
            steps: leg.steps.map((step) => ({
              instruction: step.html_instructions,
              distance: step.distance.text,
              duration: step.duration.text,
            })),
          });
          fetchNearbyHotels(endLocation);
        } else {
          setTripError("No route found. Please check your locations.");
        }
        setLoadingTrip(false);
      }
    } catch (err) {
      setTripError("Error fetching trip data. Try again later.");
      setLoadingTrip(false);
    }
  };

  const fetchHotelsAlongRoute = async (route) => {
    setLoadingHotels(true);
    try {
      const response = await fetch(`${APP_URLS.HOTELS}`);
      const allHotels = await response.json();

      if (window.google && window.google.maps) {
        // Collect all cities mentioned in the route legs/steps
        const routeLeg = route.legs[0];
        const routeCities = new Set();
        
        // Add start and end cities (though we mostly want intermediate)
        if (routeLeg.start_address) routeCities.add(routeLeg.start_address.split(",")[0].trim().toLowerCase());
        if (routeLeg.end_address) routeCities.add(routeLeg.end_address.split(",")[0].trim().toLowerCase());

        routeLeg.steps.forEach(step => {
          // Extract potential city names from instructions (this is heuristic)
          const instruction = step.instructions.toLowerCase();
          // Look for city names in the instruction text
          allHotels.forEach(hotel => {
            const city = hotel.city?.toLowerCase();
            if (city && instruction.includes(city)) {
              routeCities.add(city);
            }
          });
        });

        const alongRoute = allHotels.filter((hotel) => {
          const hotelCity = hotel.city?.toLowerCase();
          if (!hotelCity) return false;

          // Exclude destination city as it has its own section
          const destinationCity = endLocation.split(",")[0].trim().toLowerCase();
          if (hotelCity === destinationCity) return false;

          return routeCities.has(hotelCity);
        });
        
        setHotelsAlongRoute(alongRoute);
        if (alongRoute.length > 0) {
          await fetchHotelPhotosForList(alongRoute);
          await fetchMinPrices(alongRoute);
        }
      }
    } catch (error) {
      console.error("Error finding hotels along route:", error);
    } finally {
      setLoadingHotels(false);
    }
  };

  // Fetch hotel photos for hotels
  const fetchHotelPhotosForList = async (hotels) => {
    const photoPromises = hotels.map(async (hotel) => {
      try {
        const photosResponse = await axios.get(
          `${APP_URLS.API}/hotel-photos/${hotel.hotelId}`,
        );
        return {
          hotelId: hotel.hotelId,
          photos: photosResponse.data || [],
        };
      } catch (error) {
        return { hotelId: hotel.hotelId, photos: [] };
      }
    });

    const photoResults = await Promise.all(photoPromises);
    const photosMap = {};
    photoResults.forEach((result) => {
      photosMap[result.hotelId] = result.photos;
    });
    setHotelPhotos(photosMap);
  };

  // Fetch minimum room prices for hotels
  const fetchMinPrices = async (hotels) => {
    const pricePromises = hotels.map(async (hotel) => {
      try {
        const roomsResponse = await axios.get(
          `${APP_URLS.HOTELS}/${hotel.hotelId}/rooms`,
        );
        if (roomsResponse.data && roomsResponse.data.length > 0) {
          const prices = roomsResponse.data.map((room) => room.pricePerNight || 0);
          const minPrice = Math.min(...prices);
          return { hotelId: hotel.hotelId, minPrice };
        }
        return { hotelId: hotel.hotelId, minPrice: null };
      } catch (error) {
        return { hotelId: hotel.hotelId, minPrice: null };
      }
    });

    const priceResults = await Promise.all(pricePromises);
    const pricesMap = {};
    priceResults.forEach((result) => {
      pricesMap[result.hotelId] = result.minPrice;
    });
    setHotelMinPrices(pricesMap);
  };

  // Fetch nearby hotels based on destination
  const fetchNearbyHotels = async (location) => {
    setLoadingHotels(true);
    setHotelsError("");

    try {
      const response = await fetch(`${APP_URLS.HOTELS}`);
      const allHotels = await response.json();

      // Filter hotels by destination city (simple matching)
      const locationCity = location.split(",")[0].trim().toLowerCase();
      const filtered = allHotels.filter(
        (hotel) =>
          hotel.city?.toLowerCase().includes(locationCity) ||
          hotel.address?.toLowerCase().includes(locationCity),
      );

      if (filtered.length > 0) {
        setNearbyHotels(filtered);
        await fetchHotelPhotosForList(filtered);
        await fetchMinPrices(filtered);
      } else {
        // If no exact match, show all hotels as fallback
        const fallbackHotels = allHotels.slice(0, 6);
        setNearbyHotels(fallbackHotels);
        await fetchHotelPhotosForList(fallbackHotels);
        await fetchMinPrices(fallbackHotels);
        setHotelsError(
          `Showing all available hotels (No hotels found specifically in ${locationCity})`,
        );
      }
    } catch (error) {
      setHotelsError("Unable to fetch nearby hotels. Please try again.");
      console.error("Error fetching hotels:", error);
    }

    setLoadingHotels(false);
  };

  const destinations = [
    {
      id: 1,
      name: "Taj Mahal, Agra",
      image: "https://images.unsplash.com/photo-1564507592333-c60657eea523",
      description: "Symbol of eternal love, architectural marvel",
      bestTime: "October to March",
      attractions: ["Taj Mahal", "Agra Fort", "Fatehpur Sikri"],
      route: "Delhi → Agra via Yamuna Expressway",
    },
    {
      id: 2,
      name: "Varanasi, Uttar Pradesh",
      image: "https://images.unsplash.com/photo-1561361513-2d000a50f0dc",
      description: "Spiritual capital of India",
      bestTime: "October to March",
      attractions: ["Ganges Ghats", "Kashi Vishwanath Temple", "Evening Aarti"],
      route: "Delhi → Varanasi via Flight",
    },
    {
      id: 3,
      name: "Kerala Backwaters",
      image: "https://images.unsplash.com/photo-1602216056096-3b40cc0c9944",
      description: "Serene waterways and houseboats",
      bestTime: "September to March",
      attractions: ["Alleppey", "Kumarakom", "Houseboat Stay"],
      route: "Kochi → Alleppey via Road",
    },
  ];

  const travelTips = [
    {
      id: 1,
      icon: FaMoneyBillWave,
      title: "Smart Budget Planning",
      description: "Save money without compromising on experiences",
      tips: [
        "Book flights and trains 2-3 months in advance for best deals",
        "Travel during off-peak seasons (April-June, July-September)",
        "Use local public transport - Metro, buses, and shared autos",
        "Stay in budget hotels, hostels, or homestays for authentic experiences",
        "Eat at local dhabas and street food stalls for affordable meals",
      ],
    },
    {
      id: 2,
      icon: FaUmbrellaBeach,
      title: "Best Time to Visit",
      description: "Plan according to seasons and festivals",
      tips: [
        "Winter (Oct-Mar): Ideal for North India, Rajasthan, and beaches",
        "Summer (Apr-Jun): Perfect for hill stations like Shimla, Manali",
        "Monsoon (Jul-Sep): Beautiful in Kerala, Goa, and Western Ghats",
        "Plan around festivals like Diwali, Holi, or Durga Puja",
        "Avoid peak tourist season for better prices and fewer crowds",
      ],
    },
    {
      id: 3,
      icon: FaPassport,
      title: "Essential Documents",
      description: "Keep your paperwork in order",
      tips: [
        "Carry valid photo ID (Aadhar, PAN, Passport, or Driving License)",
        "Keep digital and physical copies of all important documents",
        "For foreign travelers: Valid visa and passport mandatory",
        "Hotel confirmations and travel tickets (printed or digital)",
        "Travel insurance documents and emergency contact numbers",
      ],
    },
    {
      id: 4,
      icon: FaCamera,
      title: "Photography & Etiquette",
      description: "Capture memories responsibly",
      tips: [
        "Ask permission before photographing locals or religious sites",
        "Respect 'No Photography' signs at temples and monuments",
        "Sunrise and sunset offer the best natural lighting",
        "Carry extra batteries and memory cards for longer trips",
        "Be mindful of privacy and cultural sensitivities",
      ],
    },
    {
      id: 5,
      icon: FaShieldAlt,
      title: "Safety & Security",
      description: "Stay safe throughout your journey",
      tips: [
        "Share your itinerary with family and friends",
        "Avoid traveling alone late at night in unfamiliar areas",
        "Keep valuables in hotel safes; use anti-theft bags",
        "Stay alert in crowded places - watch for pickpockets",
        "Register with embassy/consulate if traveling from abroad",
      ],
    },
    {
      id: 6,
      icon: FaUtensils,
      title: "Food & Dining",
      description: "Enjoy local cuisine safely",
      tips: [
        "Start with mild dishes if you're not used to spicy food",
        "Drink only bottled or filtered water; avoid ice in drinks",
        "Eat at busy restaurants - high turnover means fresh food",
        "Try regional specialties - every state has unique cuisines",
        "Carry digestive tablets and ORS for stomach issues",
      ],
    },
    {
      id: 7,
      icon: FaLanguage,
      title: "Cultural Respect",
      description: "Embrace local customs and traditions",
      tips: [
        "Dress modestly at religious sites - cover shoulders and knees",
        "Remove shoes before entering temples, mosques, and homes",
        "Use your right hand for eating, giving, and receiving",
        "Learn basic Hindi phrases: Namaste, Dhanyavaad (Thank you)",
        "Respect local customs during festivals and religious ceremonies",
      ],
    },
    {
      id: 8,
      icon: FaFirstAid,
      title: "Health & Wellness",
      description: "Stay healthy during your travels",
      tips: [
        "Carry a basic first-aid kit with band-aids and medicines",
        "Get necessary vaccinations before traveling to India",
        "Use sunscreen (SPF 30+) and stay hydrated in summer",
        "Have travel insurance covering medical emergencies",
        "Keep emergency numbers: Police (100), Ambulance (108)",
      ],
    },
  ];

  return (
    <div
      className="min-h-screen bg-white"
      style={{
        fontFamily: "'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      }}
    >
      {/* Hero Section - Compact */}
      <style>{`
        .pac-container {
          z-index: 10000 !important;
          border-radius: 8px;
          box-shadow: 0 4px 20px rgba(0,0,0,0.15);
          border: 1px solid #eee;
          margin-top: 4px;
        }
        input:-webkit-autofill,
        input:-webkit-autofill:hover, 
        input:-webkit-autofill:focus {
          -webkit-text-fill-color: #1F2937;
          -webkit-box-shadow: 0 0 0px 1000px white inset;
          transition: background-color 5000s ease-in-out 0s;
        }
      `}</style>
      <header className="relative h-[280px] md:h-[320px] overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1524492412937-b28074a5d7da"
          alt="India Cultural"
          className="w-full h-full object-cover"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(135deg, rgba(238, 46, 36, 0.85) 0%, rgba(255, 196, 0, 0.75) 100%)",
          }}
        />
        <div className="absolute inset-0 flex flex-col items-center justify-center text-white p-4">
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              background: "rgba(255, 255, 255, 0.2)",
              padding: "4px 12px",
              borderRadius: "16px",
              marginBottom: "12px",
              fontSize: "0.8rem",
              fontWeight: "600",
            }}
          >
            <FaMapMarkerAlt style={{ fontSize: "0.85rem" }} />
            Trivana Travel
          </div>
          <h1
            className="text-3xl md:text-4xl font-bold mb-2 text-center"
            style={{
              letterSpacing: "-0.8px",
              textShadow: "0 2px 20px rgba(0,0,0,0.3)",
              fontWeight: "800",
            }}
          >
            Explore India – Your Ultimate Travel Guide
          </h1>
          <p
            className="text-base md:text-lg text-center max-w-2xl"
            style={{
              opacity: 0.95,
              fontWeight: "500",
              textShadow: "0 1px 10px rgba(0,0,0,0.2)",
            }}
          >
            Discover the endless beauty, rich culture, and diverse landscapes of
            incredible India
          </p>
        </div>
      </header>

      {/* Trip Planner Search Section */}
      <div
        className="container mx-auto"
        style={{
          marginTop: window.innerWidth < 768 ? "-40px" : "-60px",
          position: "relative",
          zIndex: 2,
          padding: window.innerWidth < 768 ? "0 12px" : "0 16px",
        }}
      >
        <div
          className="max-w-5xl mx-auto"
          style={{
            background: "#FFFFFF",
            borderRadius: window.innerWidth < 768 ? "12px" : "14px",
            padding: "0",
            boxShadow:
              "0 20px 60px rgba(0, 0, 0, 0.25), 0 8px 20px rgba(0, 0, 0, 0.12)",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              padding:
                window.innerWidth < 768 ? "18px 20px 16px" : "24px 28px 20px",
              background:
                "linear-gradient(135deg, rgba(238, 46, 36, 0.03) 0%, rgba(255, 196, 0, 0.03) 100%)",
              borderBottom: "1px solid rgba(238, 46, 36, 0.1)",
            }}
          >
            <h2
              style={{
                fontSize: window.innerWidth < 768 ? "1.15rem" : "1.4rem",
                fontWeight: "800",
                color: "#2E2E2E",
                letterSpacing: "-0.7px",
                margin: 0,
              }}
            >
              Plan Your Perfect Trip
            </h2>
          </div>
          <form
            onSubmit={handleTripSearch}
            style={{
              display: "grid",
              gridTemplateColumns:
                window.innerWidth < 768 ? "1fr" : "1fr 1fr auto",
              gap: "0",
              alignItems: "stretch",
            }}
          >
            {/* Starting Location */}
            <div
              style={{
                borderRight:
                  window.innerWidth < 768 ? "none" : "1px solid #E5E7EB",
                borderBottom:
                  window.innerWidth < 768 ? "1px solid #E5E7EB" : "none",
                padding: window.innerWidth < 768 ? "16px 20px" : "20px 24px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              <label
                style={{
                  fontSize: "0.75rem",
                  fontWeight: "600",
                  color: "#64748B",
                  marginBottom: "6px",
                  letterSpacing: "0.5px",
                  textTransform: "uppercase",
                }}
              >
                From
              </label>
              <input
                type="text"
                placeholder="Starting Location"
                style={{
                  padding: "0",
                  fontSize: window.innerWidth < 768 ? "0.95rem" : "1.05rem",
                  border: "none",
                  background: "transparent",
                  color: "#1F2937",
                  outline: "none",
                  fontWeight: "500",
                  fontFamily: "'Inter', sans-serif",
                  backgroundRepeat: "no-repeat",
                }}
                value={startLocation}
                onChange={(e) => setStartLocation(e.target.value)}
                ref={startInputRef}
                autoComplete="off"
              />
            </div>

            {/* Destination */}
            <div
              style={{
                borderRight:
                  window.innerWidth < 768 ? "none" : "1px solid #E5E7EB",
                borderBottom:
                  window.innerWidth < 768 ? "1px solid #E5E7EB" : "none",
                padding: window.innerWidth < 768 ? "16px 20px" : "20px 24px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              <label
                style={{
                  fontSize: "0.75rem",
                  fontWeight: "600",
                  color: "#64748B",
                  marginBottom: "6px",
                  letterSpacing: "0.5px",
                  textTransform: "uppercase",
                }}
              >
                To
              </label>
              <input
                type="text"
                placeholder="Destination"
                style={{
                  padding: "0",
                  fontSize: window.innerWidth < 768 ? "0.95rem" : "1.05rem",
                  border: "none",
                  background: "transparent",
                  color: "#1F2937",
                  outline: "none",
                  fontWeight: "500",
                  fontFamily: "'Inter', sans-serif",
                  backgroundRepeat: "no-repeat",
                }}
                value={endLocation}
                onChange={(e) => setEndLocation(e.target.value)}
                ref={endInputRef}
                autoComplete="off"
              />
            </div>

            {/* Search Button */}
            <button
              type="submit"
              disabled={loadingTrip}
              style={{
                padding: window.innerWidth < 768 ? "16px 24px" : "20px 32px",
                background: "linear-gradient(135deg, #EE2E24 0%, #D82820 100%)",
                color: "white",
                border: "none",
                fontSize: window.innerWidth < 768 ? "0.95rem" : "1rem",
                fontWeight: "700",
                cursor: loadingTrip ? "wait" : "pointer",
                transition: "all 0.3s ease",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "10px",
                letterSpacing: "-0.3px",
                whiteSpace: "nowrap",
                width: window.innerWidth < 768 ? "100%" : "auto",
              }}
              onMouseEnter={(e) => {
                if (!loadingTrip) {
                  e.target.style.background =
                    "linear-gradient(135deg, #D82820 0%, #C01F18 100%)";
                  e.target.style.transform = "scale(1.02)";
                }
              }}
              onMouseLeave={(e) => {
                if (!loadingTrip) {
                  e.target.style.background =
                    "linear-gradient(135deg, #EE2E24 0%, #D82820 100%)";
                  e.target.style.transform = "scale(1)";
                }
              }}
            >
              <FaSearch style={{ fontSize: "1.1rem" }} />
              {loadingTrip ? "Searching..." : "Plan Trip"}
            </button>
          </form>

          {/* Error Message */}
          {tripError && (
            <div
              style={{
                margin: "0 28px 24px",
                padding: "14px 16px",
                borderRadius: "8px",
                background: "rgba(239, 68, 68, 0.08)",
                border: "1px solid rgba(239, 68, 68, 0.2)",
                color: "#DC2626",
                fontSize: "0.9rem",
                fontWeight: "600",
                display: "flex",
                alignItems: "center",
                gap: "10px",
              }}
            >
              <span>⚠️</span>
              {tripError}
            </div>
          )}
        </div>
      </div>

      {/* Destinations Section */}
      <section
        className="container mx-auto px-4"
        style={{ marginTop: "48px", paddingBottom: "40px" }}
      >
        <h2
          style={{
            fontSize: "clamp(1.5rem, 3.5vw, 2rem)",
            fontWeight: "800",
            marginBottom: "28px",
            textAlign: "center",
            color: "#2E2E2E",
            letterSpacing: "-1px",
          }}
        >
          Popular Destinations
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {destinations.map((dest) => (
            <div
              key={dest.id}
              className="bg-white rounded-lg overflow-hidden transform transition-all duration-300"
              style={{
                boxShadow: "0 3px 12px rgba(0,0,0,0.08)",
                border: "1px solid rgba(238, 46, 36, 0.1)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-6px)";
                e.currentTarget.style.boxShadow =
                  "0 10px 25px rgba(238, 46, 36, 0.15)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 3px 12px rgba(0,0,0,0.08)";
              }}
            >
              <div
                className="relative overflow-hidden"
                style={{ height: "220px" }}
              >
                <img
                  src={dest.image}
                  alt={dest.name}
                  className="w-full h-full object-cover transition-transform duration-500"
                  onMouseEnter={(e) =>
                    (e.target.style.transform = "scale(1.1)")
                  }
                  onMouseLeave={(e) => (e.target.style.transform = "scale(1)")}
                />
                <div
                  className="absolute top-3 right-3 px-3 py-1 rounded-full text-xs font-bold"
                  style={{
                    background: "#FFC400",
                    color: "#2E2E2E",
                  }}
                >
                  Featured
                </div>
              </div>
              <div className="p-6">
                <h3
                  className="text-xl font-bold mb-2"
                  style={{
                    color: "#2E2E2E",
                    fontWeight: "800",
                    letterSpacing: "-0.5px",
                    fontSize: "1.15rem",
                  }}
                >
                  {dest.name}
                </h3>
                <p
                  className="mb-4"
                  style={{
                    color: "#64748B",
                    fontSize: "0.9rem",
                    lineHeight: "1.6",
                  }}
                >
                  {dest.description}
                </p>
                <div className="space-y-2" style={{ fontSize: "0.875rem" }}>
                  <p
                    className="flex items-center"
                    style={{ color: "#475569", fontWeight: "500" }}
                  >
                    <FaCalendarAlt
                      className="mr-2"
                      style={{ color: "#EE2E24" }}
                    />
                    Best Time: {dest.bestTime}
                  </p>
                  <p
                    className="flex items-center"
                    style={{ color: "#475569", fontWeight: "500" }}
                  >
                    <FaMapMarkerAlt
                      className="mr-2"
                      style={{ color: "#EE2E24" }}
                    />
                    Route: {dest.route}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setEndLocation(dest.name);
                    window.scrollTo({ top: 100, behavior: "smooth" });
                  }}
                  className="mt-4 w-full py-2.5 rounded-lg font-bold transition-all duration-300"
                  style={{
                    border: "2px solid #EE2E24",
                    color: "#EE2E24",
                    background: "white",
                    fontSize: "0.9rem",
                    fontWeight: "700",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = "#EE2E24";
                    e.target.style.color = "white";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = "white";
                    e.target.style.color = "#EE2E24";
                  }}
                >
                  Plan Trip Now
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Hotels Along the Route Section */}
      {/* Hotels Along Route Section - SHOW FIRST */}
      {hotelsAlongRoute.length > 0 && (
        <section style={{ padding: "60px 0 40px", background: "#FFFFFF", borderBottom: "2px solid #f0f0f0" }}>
          <div className="container mx-auto px-4 max-w-7xl">
            <div className="text-center mb-10">
              <h2
                style={{
                  fontSize: "clamp(1.75rem, 4vw, 2.5rem)",
                  fontWeight: "800",
                  marginBottom: "12px",
                  color: "#2E2E2E",
                  letterSpacing: "-1px",
                }}
              >
                🏨 Hotels Along Your Route
              </h2>
              <p
                style={{
                  color: "#64748B",
                  fontWeight: "500",
                  fontSize: "1rem",
                  maxWidth: "700px",
                  margin: "0 auto",
                }}
              >
                Perfect stops on your journey from{" "}
                <span style={{ color: "#EE2E24", fontWeight: "700" }}>{startLocation}</span> to{" "}
                <span style={{ color: "#EE2E24", fontWeight: "700" }}>{endLocation}</span>
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {hotelsAlongRoute.map((hotel) => {
                const minPrice = hotelMinPrices[hotel.hotelId];
                const photos = hotelPhotos[hotel.hotelId] || [];
                
                return (
                  <div
                    key={`along-${hotel.hotelId}`}
                    onClick={() => navigate('/HotelRoomsPage', { state: { selectedHotelId: hotel.hotelId } })}
                    className="bg-white rounded-xl overflow-hidden transform transition-all duration-300"
                    style={{
                      boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
                      border: "1px solid #E5E7EB",
                      cursor: "pointer",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-8px)";
                      e.currentTarget.style.boxShadow = "0 12px 30px rgba(238, 46, 36, 0.2)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "0 4px 15px rgba(0,0,0,0.1)";
                    }}
                  >
                    {/* Image Gallery Section */}
                    <div
                      style={{
                        position: "relative",
                        height: "220px",
                        display: "flex",
                        overflow: "hidden",
                      }}
                    >
                      {/* Main Image - 70% */}
                      <div style={{ flex: "0 0 70%", position: "relative" }}>
                        <img
                          src={
                            photos[0]?.photoUrl ||
                            "https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800&auto=format&fit=crop&q=80"
                          }
                          alt={hotel.hotelName}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            transition: "transform 0.3s ease",
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.05)")}
                          onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
                        />
                        {/* "On Your Way" Badge */}
                        <div
                          style={{
                            position: "absolute",
                            top: "12px",
                            left: "12px",
                            background: "rgba(238, 46, 36, 0.95)",
                            padding: "6px 12px",
                            borderRadius: "6px",
                            fontSize: "0.7rem",
                            fontWeight: "700",
                            color: "white",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                          }}
                        >
                          🛣️ On Your Way
                        </div>
                      </div>

                      {/* Thumbnail Images - 30% */}
                      <div
                        style={{
                          flex: "0 0 30%",
                          display: "flex",
                          flexDirection: "column",
                          gap: "2px",
                        }}
                      >
                        {(photos.slice(1, 4).length > 0
                          ? photos.slice(1, 4)
                          : [
                              {
                                photoUrl:
                                  "https://images.unsplash.com/photo-1542314831-068cd1dbfeeb?w=400",
                              },
                              {
                                photoUrl:
                                  "https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?w=400",
                              },
                              {
                                photoUrl:
                                  "https://images.unsplash.com/photo-1445019980597-93fa8acb246c?w=400",
                              },
                            ]
                        ).map((photo, idx) => (
                          <div
                            key={idx}
                            style={{
                              flex: "1 1 0",
                              overflow: "hidden",
                              position: "relative",
                              minHeight: 0,
                            }}
                          >
                            <img
                              src={photo.photoUrl}
                              alt={`View ${idx + 1}`}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                transition: "transform 0.2s ease",
                              }}
                              onMouseEnter={(e) =>
                                (e.currentTarget.style.transform = "scale(1.1)")
                              }
                              onMouseLeave={(e) =>
                                (e.currentTarget.style.transform = "scale(1)")
                              }
                            />
                            {idx === 2 && photos.length > 4 && (
                              <div
                                style={{
                                  position: "absolute",
                                  top: 0,
                                  left: 0,
                                  right: 0,
                                  bottom: 0,
                                  background: "rgba(0, 0, 0, 0.7)",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  color: "#fff",
                                  fontSize: "0.75rem",
                                  fontWeight: "700",
                                }}
                              >
                                +{photos.length - 3} more
                              </div>
                            )}
                          </div>
                        ))}</div>
                    </div>

                    {/* Hotel Details */}
                    <div style={{ padding: "20px" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: "10px" }}>
                        <h3
                          style={{
                            fontSize: "1.25rem",
                            fontWeight: "700",
                            color: "#1A1A1A",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            flex: 1,
                            marginRight: "10px",
                          }}
                        >
                          {hotel.hotelName}
                        </h3>
                        {/* Veg/Non-Veg Badge */}
                        {(() => {
                          const foodType = hotel?.foodType || hotel?.food_type;
                          if (!foodType) return null;
                          const isPureVeg = foodType === "Pure Veg";
                          return (
                            <div
                              style={{
                                background: isPureVeg
                                  ? "linear-gradient(135deg, #00A550 0%, #059669 100%)"
                                  : "linear-gradient(135deg, #DC2626 0%, #991B1B 100%)",
                                padding: "4px 10px",
                                borderRadius: "20px",
                                fontSize: "0.65rem",
                                fontWeight: "700",
                                color: "#FFFFFF",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "4px",
                                boxShadow: isPureVeg
                                  ? "0 2px 8px rgba(0,165,80,0.25)"
                                  : "0 2px 8px rgba(220,38,38,0.25)",
                                whiteSpace: "nowrap",
                                letterSpacing: "0.3px",
                                textTransform: "uppercase",
                                border: "1.5px solid rgba(255,255,255,0.3)",
                              }}
                            >
                              <span style={{ fontSize: "0.6rem", lineHeight: 1 }}>●</span>
                              {foodType}
                            </div>
                          );
                        })()}
                      </div>

                      {/* Location - Full Address */}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "start",
                          marginBottom: "12px",
                          color: "#1E293B",
                          fontSize: "0.85rem",
                          fontWeight: "500",
                        }}
                      >
                        <FaMapMarkerAlt
                          style={{
                            marginRight: "6px",
                            marginTop: "3px",
                            color: "#EE2E24",
                            flexShrink: 0,
                            fontSize: "0.9rem",
                          }}
                        />
                        <span style={{ lineHeight: "1.5", color: "#475569" }}>
                          {hotel.address || [hotel.city, hotel.state].filter(Boolean).join(", ") || "Address not available"}
                        </span>
                      </div>

                      {/* View on Map Button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent((hotel.hotelName || "") + " " + (hotel.city || hotel.address || ""))}`, '_blank');
                        }}
                        style={{
                          display: "flex", alignItems: "center", gap: "6px",
                          padding: "5px 12px", borderRadius: "20px",
                          border: "1.5px solid #EE2E24", background: "#fff",
                          color: "#EE2E24", fontSize: "0.75rem", fontWeight: "800",
                          cursor: "pointer", whiteSpace: "nowrap", marginBottom: "8px"
                        }}
                      >
                        <FaMapMarkerAlt size={11} /> View on Map
                      </button>

                      {/* Divider */}
                      <div
                        style={{
                          height: "1px",
                          background: "#E5E7EB",
                          margin: "12px 0",
                        }}
                      />

                      {/* Price & Book Button */}
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        {minPrice && (
                          <div>
                            <div
                              style={{
                                fontSize: "1.5rem",
                                fontWeight: "700",
                                color: "#1A1A1A",
                              }}
                            >
                              ₹{minPrice}
                            </div>
                            <div style={{ fontSize: "0.7rem", color: "#9CA3AF" }}>
                              per night
                            </div>
                          </div>
                        )}
                        <button
                          style={{
                            padding: "10px 20px",
                            background: "linear-gradient(135deg, #EE2E24 0%, #FFC400 100%)",
                            color: "white",
                            border: "none",
                            borderRadius: "8px",
                            fontWeight: "700",
                            fontSize: "0.9rem",
                            cursor: "pointer",
                            transition: "all 0.3s ease",
                            boxShadow: "0 2px 8px rgba(238, 46, 36, 0.3)",
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate('/HotelRoomsPage', { state: { selectedHotelId: hotel.hotelId } });
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.transform = "scale(1.05)";
                            e.target.style.boxShadow = "0 4px 12px rgba(238, 46, 36, 0.4)";
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.transform = "scale(1)";
                            e.target.style.boxShadow = "0 2px 8px rgba(238, 46, 36, 0.3)";
                          }}
                        >
                          View Details
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Nearby Hotels Section - Show after hotels along route */}
      {(nearbyHotels.length > 0 || loadingHotels) && (
        <section style={{ padding: "60px 0", background: "#f8f9fa", borderBottom: "2px solid #f0f0f0" }}>
          <div className="container mx-auto px-4 max-w-7xl">
            <div className="text-center mb-10">
              <h2
                style={{
                  fontSize: "clamp(1.75rem, 4vw, 2.5rem)",
                  fontWeight: "800",
                  marginBottom: "12px",
                  color: "#2E2E2E",
                  letterSpacing: "-1px",
                }}
              >
                🎯 Hotels at Your Destination
              </h2>
              <p
                style={{
                  color: "#64748B",
                  fontWeight: "500",
                  fontSize: "1rem",
                  maxWidth: "700px",
                  margin: "0 auto",
                }}
              >
                Top-rated stays in{" "}
                <span style={{ color: "#EE2E24", fontWeight: "700" }}>
                  {endLocation}
                </span>
              </p>
              {hotelsError && (
                <p
                  style={{
                    color: "#FFA500",
                    fontSize: "0.875rem",
                    marginTop: "8px",
                  }}
                >
                  {hotelsError}
                </p>
              )}
            </div>

            {loadingHotels ? (
              <div style={{ textAlign: "center", padding: "40px 0" }}>
                <div
                  style={{
                    display: "inline-block",
                    width: "50px",
                    height: "50px",
                    border: "4px solid #f3f3f3",
                    borderTop: "4px solid #EE2E24",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                  }}
                />
                <p
                  style={{
                    marginTop: "16px",
                    color: "#64748B",
                    fontWeight: "600",
                  }}
                >
                  Finding hotels for you...
                </p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {nearbyHotels.map((hotel) => {
                    const minPrice = hotelMinPrices[hotel.hotelId];
                    const photos = hotelPhotos[hotel.hotelId] || [];

                    return (
                      <div
                        key={hotel.hotelId}
                        onClick={() => navigate(`/hotel/${hotel.hotelId}`)}
                        className="bg-white rounded-xl overflow-hidden transform transition-all duration-300"
                        style={{
                          boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
                          border: "1px solid #E5E7EB",
                          cursor: "pointer",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = "translateY(-8px)";
                          e.currentTarget.style.boxShadow =
                            "0 12px 30px rgba(238, 46, 36, 0.2)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = "translateY(0)";
                          e.currentTarget.style.boxShadow =
                            "0 4px 15px rgba(0,0,0,0.1)";
                        }}
                      >
                        {/* Image Gallery Section */}
                        <div
                          style={{
                            position: "relative",
                            height: "220px",
                            display: "flex",
                            overflow: "hidden",
                          }}
                        >
                          {/* Main Image - 70% */}
                          <div style={{ flex: "0 0 70%", position: "relative" }}>
                            <img
                              src={
                                photos[0]?.photoUrl ||
                                "https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800&auto=format&fit=crop&q=80"
                              }
                              alt={hotel.hotelName}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                transition: "transform 0.3s ease",
                              }}
                              onMouseEnter={(e) =>
                                (e.currentTarget.style.transform = "scale(1.05)")
                              }
                              onMouseLeave={(e) =>
                                (e.currentTarget.style.transform = "scale(1)")
                              }
                            />
                            {/* Food Type Badge */}
                            {(hotel.foodType || hotel.food_type) && (
                              <div
                                style={{
                                  position: "absolute",
                                  top: "12px",
                                  left: "12px",
                                  background:
                                    (hotel.foodType || hotel.food_type) === "Pure Veg"
                                      ? "linear-gradient(135deg, #00A550 0%, #059669 100%)"
                                      : "linear-gradient(135deg, #DC2626 0%, #991B1B 100%)",
                                  padding: "6px 12px",
                                  borderRadius: "6px",
                                  fontSize: "0.7rem",
                                  fontWeight: "700",
                                  color: "#FFFFFF",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.5px",
                                  boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                                }}
                              >
                                {hotel.foodType || hotel.food_type}
                              </div>
                            )}
                          </div>

                          {/* Thumbnail Images - 30% */}
                          <div
                            style={{
                              flex: "0 0 30%",
                              display: "flex",
                              flexDirection: "column",
                              gap: "2px",
                            }}
                          >
                            {(photos.slice(1, 4).length > 0
                              ? photos.slice(1, 4)
                              : [
                                  {
                                    photoUrl:
                                      "https://images.unsplash.com/photo-1542314831-068cd1dbfeeb?w=400",
                                  },
                                  {
                                    photoUrl:
                                      "https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?w=400",
                                  },
                                  {
                                    photoUrl:
                                      "https://images.unsplash.com/photo-1445019980597-93fa8acb246c?w=400",
                                  },
                                ]
                            ).map((photo, idx) => (
                              <div
                                key={idx}
                                style={{
                                  flex: "1 1 0",
                                  overflow: "hidden",
                                  position: "relative",
                                  minHeight: 0,
                                }}
                              >
                                <img
                                  src={photo.photoUrl}
                                  alt={`View ${idx + 1}`}
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                    transition: "transform 0.2s ease",
                                  }}
                                  onMouseEnter={(e) =>
                                    (e.currentTarget.style.transform = "scale(1.1)")
                                  }
                                  onMouseLeave={(e) =>
                                    (e.currentTarget.style.transform = "scale(1)")
                                  }
                                />
                                {idx === 2 && photos.length > 4 && (
                                  <div
                                    style={{
                                      position: "absolute",
                                      top: 0,
                                      left: 0,
                                      right: 0,
                                      bottom: 0,
                                      background: "rgba(0, 0, 0, 0.7)",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      color: "#fff",
                                      fontSize: "0.75rem",
                                      fontWeight: "700",
                                    }}
                                  >
                                    +{photos.length - 3} more
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Hotel Details */}
                        <div style={{ padding: "20px" }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: "10px" }}>
                            <h3
                              style={{
                                fontSize: "1.25rem",
                                fontWeight: "700",
                                color: "#1A1A1A",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                flex: 1,
                                marginRight: "10px",
                              }}
                            >
                              {hotel.hotelName || hotel.hotel_name || "Hotel"}
                            </h3>
                            {/* Veg/Non-Veg Badge */}
                            {(() => {
                              const foodType = hotel?.foodType || hotel?.food_type;
                              if (!foodType) return null;
                              const isPureVeg = foodType === "Pure Veg";
                              return (
                                <div
                                  style={{
                                    background: isPureVeg
                                      ? "linear-gradient(135deg, #00A550 0%, #059669 100%)"
                                      : "linear-gradient(135deg, #DC2626 0%, #991B1B 100%)",
                                    padding: "4px 10px",
                                    borderRadius: "20px",
                                    fontSize: "0.65rem",
                                    fontWeight: "700",
                                    color: "#FFFFFF",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "4px",
                                    boxShadow: isPureVeg
                                      ? "0 2px 8px rgba(0,165,80,0.25)"
                                      : "0 2px 8px rgba(220,38,38,0.25)",
                                    whiteSpace: "nowrap",
                                    letterSpacing: "0.3px",
                                    textTransform: "uppercase",
                                    border: "1.5px solid rgba(255,255,255,0.3)",
                                  }}
                                >
                                  <span style={{ fontSize: "0.6rem", lineHeight: 1 }}>●</span>
                                  {foodType}
                                </div>
                              );
                            })()}
                          </div>

                          {/* Location - Full Address */}
                          <div
                            style={{
                              display: "flex",
                              alignItems: "start",
                              marginBottom: "12px",
                              color: "#1E293B",
                              fontSize: "0.85rem",
                              fontWeight: "500",
                            }}
                          >
                            <FaMapMarkerAlt
                              style={{
                                marginRight: "6px",
                                marginTop: "3px",
                                color: "#EE2E24",
                                flexShrink: 0,
                                fontSize: "0.9rem",
                              }}
                            />
                            <span style={{ lineHeight: "1.5", color: "#475569" }}>
                              {hotel.address || [hotel.city, hotel.state].filter(Boolean).join(", ") || "Address not available"}
                            </span>
                          </div>

                          {/* View on Map Button */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent((hotel.hotelName || "") + " " + (hotel.city || hotel.address || ""))}`, '_blank');
                            }}
                            style={{
                              display: "flex", alignItems: "center", gap: "6px",
                              padding: "5px 12px", borderRadius: "20px",
                              border: "1.5px solid #EE2E24", background: "#fff",
                              color: "#EE2E24", fontSize: "0.75rem", fontWeight: "800",
                              cursor: "pointer", whiteSpace: "nowrap", marginBottom: "8px"
                            }}
                          >
                            <FaMapMarkerAlt size={11} /> View on Map
                          </button>

                          {/* Divider */}
                          <div
                            style={{
                              height: "1px",
                              background: "#E5E7EB",
                              margin: "12px 0",
                            }}
                          />

                          {/* Price & Book Button */}
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            {minPrice && (
                              <div>
                                <div
                                  style={{
                                    fontSize: "1.5rem",
                                    fontWeight: "700",
                                    color: "#1A1A1A",
                                  }}
                                >
                                  ₹{minPrice}
                                </div>
                                <div style={{ fontSize: "0.7rem", color: "#9CA3AF" }}>
                                  per night
                                </div>
                              </div>
                            )}
                            <button
                              style={{
                                padding: "10px 20px",
                                background:
                                  "linear-gradient(135deg, #EE2E24 0%, #FFC400 100%)",
                                color: "white",
                                border: "none",
                                borderRadius: "8px",
                                fontWeight: "700",
                                fontSize: "0.9rem",
                                cursor: "pointer",
                                transition: "all 0.3s ease",
                                boxShadow: "0 2px 8px rgba(238, 46, 36, 0.3)",
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate('/HotelRoomsPage', { state: { selectedHotelId: hotel.hotelId } });
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.transform = "scale(1.05)";
                                e.target.style.boxShadow =
                                  "0 4px 12px rgba(238, 46, 36, 0.4)";
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.transform = "scale(1)";
                                e.target.style.boxShadow =
                                  "0 2px 8px rgba(238, 46, 36, 0.3)";
                              }}
                            >
                              View Details
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {nearbyHotels.length === 0 && !loadingHotels && (
                  <div style={{ textAlign: "center", padding: "40px 20px" }}>
                    <p style={{ color: "#6B7280", fontSize: "1.1rem" }}>
                      No hotels found. Try a different destination.
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      )}

      {/* Trip Results - Moved below hotels */}
      {tripResult && (
        <section style={{ padding: "40px 0", background: "#F8F9FA" }}>
          <div className="container mx-auto px-4 max-w-7xl">
            <div
              style={{
                padding: "30px",
                borderRadius: "12px",
                background: "#FFFFFF",
                boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
              }}
            >
              <div
                ref={mapRef}
                style={{
                  width: "100%",
                  height: "400px",
                  borderRadius: "10px",
                  marginBottom: "30px",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                }}
              ></div>
              <h3
                style={{
                  fontSize: "1.8rem",
                  fontWeight: "800",
                  color: "#EE2E24",
                  marginBottom: "24px",
                  letterSpacing: "-0.8px",
                }}
              >
                Trip Details
              </h3>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
                  gap: "20px",
                  fontSize: "1rem",
                  marginBottom: "30px",
                }}
              >
                <div
                  style={{
                    padding: "16px",
                    background: "linear-gradient(135deg, rgba(238, 46, 36, 0.05) 0%, rgba(255, 196, 0, 0.05) 100%)",
                    borderRadius: "8px",
                    border: "1px solid rgba(238, 46, 36, 0.1)",
                  }}
                >
                  <strong style={{ color: "#64748B", fontWeight: "600", display: "block", marginBottom: "6px" }}>
                    From:
                  </strong>
                  <span style={{ color: "#2E2E2E", fontWeight: "600" }}>{tripResult.start_address}</span>
                </div>
                <div
                  style={{
                    padding: "16px",
                    background: "linear-gradient(135deg, rgba(238, 46, 36, 0.05) 0%, rgba(255, 196, 0, 0.05) 100%)",
                    borderRadius: "8px",
                    border: "1px solid rgba(238, 46, 36, 0.1)",
                  }}
                >
                  <strong style={{ color: "#64748B", fontWeight: "600", display: "block", marginBottom: "6px" }}>
                    To:
                  </strong>
                  <span style={{ color: "#2E2E2E", fontWeight: "600" }}>{tripResult.end_address}</span>
                </div>
                <div
                  style={{
                    padding: "16px",
                    background: "linear-gradient(135deg, rgba(238, 46, 36, 0.05) 0%, rgba(255, 196, 0, 0.05) 100%)",
                    borderRadius: "8px",
                    border: "1px solid rgba(238, 46, 36, 0.1)",
                  }}
                >
                  <strong style={{ color: "#64748B", fontWeight: "600", display: "block", marginBottom: "6px" }}>
                    Distance:
                  </strong>
                  <span style={{ color: "#2E2E2E", fontWeight: "600" }}>{tripResult.distance}</span>
                </div>
                <div
                  style={{
                    padding: "16px",
                    background: "linear-gradient(135deg, rgba(238, 46, 36, 0.05) 0%, rgba(255, 196, 0, 0.05) 100%)",
                    borderRadius: "8px",
                    border: "1px solid rgba(238, 46, 36, 0.1)",
                  }}
                >
                  <strong style={{ color: "#64748B", fontWeight: "600", display: "block", marginBottom: "6px" }}>
                    Duration:
                  </strong>
                  <span style={{ color: "#2E2E2E", fontWeight: "600" }}>{tripResult.duration}</span>
                </div>
              </div>
              <div style={{ marginTop: "30px" }}>
                <h4
                  style={{
                    color: "#EE2E24",
                    fontWeight: "700",
                    fontSize: "1.3rem",
                    marginBottom: "20px",
                    letterSpacing: "-0.5px",
                  }}
                >
                  Route Steps:
                </h4>
                <ol
                  style={{
                    fontSize: "0.95rem",
                    marginLeft: "28px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px",
                    listStyleType: "decimal",
                  }}
                >
                  {tripResult.steps.map((step, idx) => (
                    <li
                      key={idx}
                      style={{
                        color: "#475569",
                        lineHeight: "1.7",
                        fontWeight: "500",
                        padding: "10px 0",
                        borderBottom: idx < tripResult.steps.length - 1 ? "1px solid #E5E7EB" : "none",
                      }}
                    >
                      <span
                        dangerouslySetInnerHTML={{ __html: step.instruction }}
                      />
                      <span
                        style={{
                          marginLeft: "10px",
                          fontSize: "0.87rem",
                          color: "#64748B",
                          fontWeight: "600",
                        }}
                      >
                        ({step.distance}, {step.duration})
                      </span>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Travel Tips Section - Compact */}
      <section
        style={{
          padding: "40px 0",
          background:
            "linear-gradient(135deg, rgba(238, 46, 36, 0.03) 0%, rgba(255, 196, 0, 0.03) 100%)",
        }}
      >
        <div className="container mx-auto px-4">
          <div className="text-center mb-6">
            <h2
              style={{
                fontSize: "clamp(1.5rem, 3.5vw, 2rem)",
                fontWeight: "800",
                marginBottom: "6px",
                color: "#2E2E2E",
                letterSpacing: "-1px",
              }}
            >
              Essential Travel Tips
            </h2>
            <p
              style={{
                color: "#64748B",
                fontWeight: "500",
                fontSize: "0.875rem",
                maxWidth: "600px",
                margin: "0 auto",
              }}
            >
              Click any card to view detailed tips for your journey
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 max-w-7xl mx-auto">
            {travelTips.map((tip) => {
              const IconComponent = tip.icon;
              return (
                <div
                  key={tip.id}
                  className="bg-white rounded-lg overflow-hidden transform transition-all duration-300"
                  style={{
                    boxShadow: "0 3px 12px rgba(0,0,0,0.08)",
                    border: "1px solid rgba(238, 46, 36, 0.1)",
                    cursor: "pointer",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-6px)";
                    e.currentTarget.style.boxShadow =
                      "0 10px 25px rgba(238, 46, 36, 0.15)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow =
                      "0 3px 12px rgba(0,0,0,0.08)";
                  }}
                  onClick={() =>
                    setExpandedTip(expandedTip === tip.id ? null : tip.id)
                  }
                >
                  {/* Card Header with Icon - Compact */}
                  <div
                    style={{
                      padding: "14px 16px",
                      background:
                        expandedTip === tip.id
                          ? "linear-gradient(135deg, rgba(238, 46, 36, 0.08) 0%, rgba(255, 196, 0, 0.08) 100%)"
                          : "white",
                      transition: "all 0.3s ease",
                    }}
                  >
                    <div className="flex items-center gap-2.5 mb-1.5">
                      <div
                        className="flex items-center justify-center rounded-full flex-shrink-0"
                        style={{
                          width: "38px",
                          height: "38px",
                          background:
                            "linear-gradient(135deg, #EE2E24 0%, #FFC400 100%)",
                          boxShadow: "0 2px 8px rgba(238, 46, 36, 0.25)",
                        }}
                      >
                        <IconComponent
                          style={{ fontSize: "1.05rem", color: "white" }}
                        />
                      </div>
                      <h3
                        className="font-bold flex-1"
                        style={{
                          color: "#2E2E2E",
                          fontWeight: "800",
                          fontSize: "0.9rem",
                          letterSpacing: "-0.5px",
                          lineHeight: "1.3",
                        }}
                      >
                        {tip.title}
                      </h3>
                      <div
                        style={{
                          color: "#EE2E24",
                          transition: "transform 0.3s ease",
                          transform:
                            expandedTip === tip.id
                              ? "rotate(180deg)"
                              : "rotate(0deg)",
                        }}
                      >
                        <MdExpandMore size={18} />
                      </div>
                    </div>
                    {!expandedTip || expandedTip !== tip.id ? (
                      <p
                        style={{
                          color: "#64748B",
                          fontSize: "0.775rem",
                          lineHeight: "1.4",
                          fontWeight: "500",
                        }}
                      >
                        {tip.description}
                      </p>
                    ) : null}
                  </div>

                  {/* Expandable Content - Compact */}
                  {expandedTip === tip.id && (
                    <div
                      style={{
                        padding: "12px 16px 14px",
                        borderTop: "1px solid rgba(238, 46, 36, 0.1)",
                        background:
                          "linear-gradient(135deg, rgba(238, 46, 36, 0.02) 0%, rgba(255, 196, 0, 0.02) 100%)",
                        animation: "fadeIn 0.3s ease",
                      }}
                    >
                      <ul
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "6px",
                        }}
                      >
                        {tip.tips.map((item, idx) => (
                          <li
                            key={idx}
                            className="flex items-start"
                            style={{ fontSize: "0.775rem", lineHeight: "1.5" }}
                          >
                            <span
                              style={{
                                color: "#EE2E24",
                                marginRight: "6px",
                                marginTop: "1px",
                                fontSize: "0.9rem",
                                fontWeight: "bold",
                              }}
                            >
                              •
                            </span>
                            <span
                              style={{
                                color: "#475569",
                                flex: 1,
                                fontWeight: "500",
                              }}
                            >
                              {item}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Accent Bar */}
                  <div
                    style={{
                      height: "3px",
                      background:
                        expandedTip === tip.id
                          ? "linear-gradient(90deg, #EE2E24 0%, #FFC400 100%)"
                          : "transparent",
                      transition: "all 0.3s ease",
                    }}
                  />
                </div>
              );
            })}
          </div>

          {/* Quick Checklist - Compact */}
          <div className="mt-6 max-w-5xl mx-auto">
            <div
              className="bg-white rounded-lg"
              style={{
                padding: "16px 20px",
                boxShadow: "0 3px 12px rgba(238, 46, 36, 0.1)",
                border: "1px solid rgba(238, 46, 36, 0.15)",
              }}
            >
              <div className="flex items-center gap-2 mb-3">
                <div
                  style={{
                    width: "3px",
                    height: "20px",
                    background:
                      "linear-gradient(135deg, #EE2E24 0%, #FFC400 100%)",
                    borderRadius: "2px",
                  }}
                />
                <h3
                  style={{
                    fontSize: "1rem",
                    color: "#2E2E2E",
                    fontWeight: "800",
                    letterSpacing: "-0.5px",
                  }}
                >
                  Quick Travel Checklist
                </h3>
              </div>
              <div
                className="grid grid-cols-2 md:grid-cols-4 gap-2.5"
                style={{ fontSize: "0.8rem" }}
              >
                <div
                  className="flex items-center gap-1.5"
                  style={{ color: "#475569", fontWeight: "500" }}
                >
                  <span style={{ color: "#22C55E", fontSize: "0.95rem" }}>
                    ✓
                  </span>
                  <span>Valid ID</span>
                </div>
                <div
                  className="flex items-center gap-1.5"
                  style={{ color: "#475569", fontWeight: "500" }}
                >
                  <span style={{ color: "#22C55E", fontSize: "0.95rem" }}>
                    ✓
                  </span>
                  <span>Insurance</span>
                </div>
                <div
                  className="flex items-center gap-1.5"
                  style={{ color: "#475569", fontWeight: "500" }}
                >
                  <span style={{ color: "#22C55E", fontSize: "0.95rem" }}>
                    ✓
                  </span>
                  <span>Shoes</span>
                </div>
                <div
                  className="flex items-center gap-1.5"
                  style={{ color: "#475569", fontWeight: "500" }}
                >
                  <span style={{ color: "#22C55E", fontSize: "0.95rem" }}>
                    ✓
                  </span>
                  <span>Clothing</span>
                </div>
                <div
                  className="flex items-center gap-1.5"
                  style={{ color: "#475569" }}
                >
                  <span style={{ color: "#22C55E", fontSize: "1rem" }}>✓</span>
                  <span>Power Bank</span>
                </div>
                <div
                  className="flex items-center gap-1.5"
                  style={{ color: "#475569" }}
                >
                  <span style={{ color: "#22C55E", fontSize: "1rem" }}>✓</span>
                  <span>First-Aid Kit</span>
                </div>
                <div
                  className="flex items-center gap-1.5"
                  style={{ color: "#475569" }}
                >
                  <span style={{ color: "#22C55E", fontSize: "1rem" }}>✓</span>
                  <span>Emergency Contacts</span>
                </div>
                <div
                  className="flex items-center gap-1.5"
                  style={{ color: "#475569" }}
                >
                  <span style={{ color: "#22C55E", fontSize: "1rem" }}>✓</span>
                  <span>SIM/Roaming</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section
        className="text-white"
        style={{
          background: "linear-gradient(135deg, #EE2E24 0%, #FFC400 100%)",
          padding: "45px 0",
        }}
      >
        <div className="container mx-auto px-4 text-center">
          <h2
            style={{
              fontSize: "clamp(1.5rem, 3.5vw, 2rem)",
              letterSpacing: "-1px",
              fontWeight: "800",
              textShadow: "0 2px 10px rgba(0,0,0,0.2)",
              marginBottom: "10px",
            }}
          >
            Ready to Start Your Journey?
          </h2>
          <p
            style={{
              opacity: 0.95,
              fontWeight: "500",
              fontSize: "1rem",
              marginBottom: "28px",
            }}
          >
            Let us help you plan the perfect Indian adventure
          </p>

          <button
            style={{
              padding: "13px 36px",
              borderRadius: "10px",
              fontWeight: "700",
              transition: "all 0.3s ease",
              background: "white",
              color: "#EE2E24",
              fontSize: "0.95rem",
              letterSpacing: "-0.3px",
              boxShadow: "0 4px 15px rgba(0,0,0,0.2)",
              border: "none",
              cursor: "pointer",
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = "translateY(-3px)";
              e.target.style.boxShadow = "0 6px 20px rgba(0,0,0,0.3)";
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "translateY(0)";
              e.target.style.boxShadow = "0 4px 15px rgba(0,0,0,0.2)";
            }}
            onClick={() => (window.location.href = "/restaurants")}
          >
            Book Your Restaurant
          </button>
        </div>
      </section>
    </div>
  );
};

export default Travel;
